import { Fragment, useCallback, useEffect, useState } from "react";
import Dashboard from "../Dashboard";
import LoadingPlaceholder from "../../../../components/ui/loading/LoadingPlaceholder";
import { useNavigate, useParams } from "react-router-dom";
import { Handler401 } from "../../../../api/utils/responseActions";
import { formatTime, getRelativeTime } from "../../../../utils/date/DateUtils";
import { fetchFilingLogs } from "../../../../api/filings/filingAPI";
import { useRefreshOnAuth } from "../../../../hooks/useRefreshOnAuth";

function FilingLogsV2() {
  const { filingUuid } = useParams();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const response = await fetchFilingLogs(filingUuid);

      if (response.status === 401) {
        Handler401(navigate);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch filing logs: ${response.status}`);
      }

      const logsArray = await response.json();

      const normalizedLogs = logsArray.map((log) => ({
        id: log.id,
        event_type: log.event_type,
        title: log.title,
        description: log.description,
        created_at: log.created_at,
        triggered_by: log.triggered_by,
        triggered_by_role: log.triggered_by_role,
        triggered_by_name: log.triggered_by_name,
        visible_to_customer: log.visible_to_taxpayer,
        visible_to_cpa: log.visible_to_expert,
      }));

      const sortedLogs = normalizedLogs.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });

      setLogs(sortedLogs);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching filing logs:", err);
      setLoading(false);
    }
  }, [filingUuid, navigate]);

  // This side effect is used for: (a) Refresh page (b) When render the page for first
  useRefreshOnAuth(fetchData);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper to get user display name
  const getUserDisplayName = (log) => {
    return log.triggered_by_name || "User";
  };

  if (loading) {
    return <LoadingPlaceholder rows={3} type="card-large" />;
  }

  return (
    <div>
      <h3
        style={{
          marginBottom: "20px",
          fontSize: "18px",
          fontWeight: "600",
          color: "#1A2127",
        }}
      >
        Filing Timeline
      </h3>
      {logs.length > 0 ? (
        <div style={{ position: "relative", paddingLeft: "32px" }}>
          {/* Vertical line */}
          <div
            style={{
              position: "absolute",
              left: "8px",
              top: "0",
              bottom: "0",
              width: "2px",
              backgroundColor: "#e0e0e0",
            }}
          />

          {/* Log items */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "40px",
            }}
          >
            {logs.map((log, index) => (
              <div
                key={log.id || index}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "16px",
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    position: "absolute",
                    left: "-24px",
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: "#004789",
                    border: "2px solid white",
                    boxShadow: "0 0 0 2px #e0e0e0",
                    flexShrink: 0,
                  }}
                />

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: "4px" }}>
                    <strong
                      style={{
                        fontSize: "14px",
                        color: "#1A2127",
                        fontWeight: "600",
                      }}
                    >
                      {log.title}
                    </strong>
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#666B6F",
                      marginBottom: "4px",
                    }}
                  >
                    {log.description}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#adb0b2",
                      display: "flex",
                      gap: "8px",
                    }}
                  >
                    <span>by {getUserDisplayName(log)}</span>
                    <span>•</span>
                    <span>{formatTime(log.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ color: "#666", fontStyle: "italic", padding: "20px" }}>
          No logs available
        </div>
      )}
    </div>
  );
}

export default FilingLogsV2;
